// Modal de integração — mockups "Modal Integração Typebot / n8n / Dialogflow
// / SGP" (900px): seletor de tipo em cards, campos do tipo escolhido à
// esquerda e o painel explicativo daquele tipo à direita.
import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import HubIcon from "@material-ui/icons/DeviceHub";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import SendIcon from "@material-ui/icons/Send";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import WebhookIcon from "@mui/icons-material/Webhook";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { toast } from "react-toastify";

import AerosDialog from "../AerosDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { validateSgpConfig, SGP_PASSWORD_OPTIONS } from "../../utils/sgp";
import useIntegrationModalStyles from "./integrationModalStyles";
import { INTEGRATION_DOCS } from "../../pages/QueueIntegration/integrationDocs";
import {
  INTEGRATION_TYPES,
  typeMeta,
  typeLabel,
  testResultLabel,
} from "../../pages/QueueIntegration/integrationRules";
import { stateText } from "../../styles/aerosTokens";
import { useTheme, alpha } from "@material-ui/core/styles";

const TYPE_ICONS = {
  typebot: SmartToyIcon,
  n8n: HubIcon,
  webhook: WebhookIcon,
  dialogflow: PsychologyIcon,
  flowbuilder: AccountTreeIcon,
  SGP: ReceiptLongIcon,
};

const INITIAL = {
  type: "typebot",
  name: "",
  projectName: "",
  jsonContent: "",
  language: "",
  urlN8N: "",
  typebotDelayMessage: 1000,
  typebotExpires: 1,
  typebotKeywordFinish: "",
  typebotKeywordRestart: "",
  typebotRestartMessage: "",
  typebotSlug: "",
  typebotUnknownMessage: "",
  // Campos do SGP, guardados dentro de `jsonContent` no backend.
  sgpIeSenha: "",
  sgpUrl: "",
  sgpWidePayToken: "",
};

const QueueIntegrationModal = ({ open, onClose, integrationId }) => {
  const classes = useIntegrationModalStyles();
  const theme = useTheme();

  const [integration, setIntegration] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const change = (campo, valor) =>
    setIntegration((atual) => ({ ...atual, [campo]: valor }));

  useEffect(() => {
    if (!open) return;
    setTestResult(null);
    async function load() {
      if (!integrationId) {
        setIntegration(INITIAL);
        return;
      }
      try {
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        // O SGP guarda os próprios campos dentro de jsonContent; sem
        // desempacotar, o formulário abriria vazio e salvaria por cima.
        let doJson = {};
        try {
          doJson = data?.jsonContent ? JSON.parse(data.jsonContent) : {};
        } catch (err) {
          doJson = {};
        }
        setIntegration({
          ...INITIAL,
          ...data,
          sgpIeSenha: doJson.sgpIeSenha || "",
          sgpUrl: doJson.sgpUrl || "",
          sgpWidePayToken: doJson.sgpWidePayToken || "",
        });
      } catch (err) {
        toastError(err);
      }
    }
    load();
  }, [integrationId, open]);

  const meta = typeMeta(integration.type);
  const ehWebhook = meta.key === "n8n" || meta.key === "webhook";

  const handleClose = () => {
    setIntegration(INITIAL);
    setTestResult(null);
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    const valores = { ...integration };

    if (meta.key === "SGP") {
      const erros = validateSgpConfig(valores);
      if (erros?.length) {
        toast.error(erros[0]);
        return;
      }
      // Os três campos do SGP viajam serializados em jsonContent.
      valores.jsonContent = JSON.stringify({
        sgpIeSenha: valores.sgpIeSenha,
        sgpUrl: valores.sgpUrl,
        sgpWidePayToken: valores.sgpWidePayToken,
      });
    }

    setSaving(true);
    try {
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, valores);
      } else {
        await api.post("/queueIntegration", valores);
      }
      toast.success(i18n.t("aeros.integrations.modal.saved"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // O POST de teste sai do servidor: o navegador não consegue chamar uma URL
  // externa arbitrária (CORS), e o payload precisa ser o mesmo do disparo real.
  const handleTestWebhook = async () => {
    if (!integrationId) {
      toast.error(i18n.t("aeros.integrations.test.saveFirst"));
      return;
    }
    if (!integration.urlN8N) {
      toast.error(i18n.t("aeros.integrations.test.needsUrl"));
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(
        `/queueIntegration/${integrationId}/test-webhook`
      );
      setTestResult(data);
    } catch (err) {
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  const handleTestSession = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await api.post("/queueIntegration/testsession", {
        projectName: integration.projectName,
        jsonContent: integration.jsonContent,
        language: integration.language,
      });
      setTestResult({ ok: true, session: true });
    } catch (err) {
      setTestResult({ ok: false, session: true });
      toastError(err);
    } finally {
      setTesting(false);
    }
  };

  const renderLabel = (texto, { required, helper } = {}) => (
    <div className={classes.label}>
      {texto}
      {required && <span className={classes.required}>*</span>}
      {helper && <span className={classes.labelHelper}>{helper}</span>}
    </div>
  );

  const campo = (nome, rotulo, extra = {}) => (
    <div>
      {renderLabel(rotulo, extra)}
      <TextField
        value={integration[nome] ?? ""}
        onChange={(e) => change(nome, e.target.value)}
        variant="outlined"
        fullWidth
        className={`${classes.input} ${extra.mono ? classes.inputMono : ""}`}
        type={extra.type || "text"}
        multiline={!!extra.multiline}
        minRows={extra.multiline || undefined}
        placeholder={extra.placeholder}
        inputProps={{ "aria-label": rotulo }}
      />
      {extra.hint && <div className={classes.hint}>{extra.hint}</div>}
    </div>
  );

  // ---------- campos por tipo ----------
  const renderFields = () => {
    if (meta.key === "typebot") {
      return (
        <>
          {campo("urlN8N", i18n.t("aeros.integrations.form.typebotUrl"), {
            required: true,
            hint: i18n.t("aeros.integrations.form.typebotUrlHelper"),
            mono: true,
            placeholder: "https://bot.suaempresa.com.br",
          })}
          <div className={classes.pair}>
            {campo("typebotSlug", i18n.t("aeros.integrations.form.typebotSlug"), {
              required: true,
              mono: true,
            })}
            {campo("typebotExpires", i18n.t("aeros.integrations.form.typebotExpires"), {
              type: "number",
            })}
          </div>
          <div className={classes.pair}>
            {campo("typebotDelayMessage", i18n.t("aeros.integrations.form.typebotDelay"), {
              type: "number",
            })}
            {campo(
              "typebotKeywordFinish",
              i18n.t("aeros.integrations.form.typebotKeywordFinish")
            )}
          </div>
          {campo(
            "typebotKeywordRestart",
            i18n.t("aeros.integrations.form.typebotKeywordRestart")
          )}
          {campo("typebotUnknownMessage", i18n.t("aeros.integrations.form.typebotUnknown"), {
            multiline: 2,
          })}
          {campo("typebotRestartMessage", i18n.t("aeros.integrations.form.typebotRestart"), {
            multiline: 2,
          })}
        </>
      );
    }

    if (ehWebhook) {
      return (
        <>
          {campo("urlN8N", i18n.t("aeros.integrations.form.n8nUrl"), {
            required: true,
            mono: true,
            placeholder: "https://n8n.suaempresa.com.br/webhook/pulse",
          })}
          <div className={classes.testBlock}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.testTitle}>
                {i18n.t("aeros.integrations.test.title")}
              </div>
              <div className={classes.testHint}>
                {i18n.t("aeros.integrations.test.hint")}
              </div>
            </div>
            <Button
              className={classes.testButton}
              startIcon={testing ? undefined : <SendIcon />}
              onClick={handleTestWebhook}
              disabled={testing}
            >
              {testing ? (
                <CircularProgress size={14} />
              ) : (
                i18n.t("aeros.integrations.test.button")
              )}
            </Button>
          </div>
          {testResult && !testResult.session && (
            <div
              className={`${classes.testResult} ${
                testResult.ok ? classes.testOk : classes.testFail
              }`}
            >
              {testResult.ok ? <CheckCircleIcon /> : <ErrorIcon />}
              <span>
                {i18n.t(
                  testResult.ok
                    ? "aeros.integrations.test.ok"
                    : "aeros.integrations.test.fail",
                  { result: testResultLabel(testResult) }
                )}
              </span>
            </div>
          )}
        </>
      );
    }

    if (meta.key === "dialogflow") {
      return (
        <>
          <div className={classes.pair}>
            {campo("projectName", i18n.t("aeros.integrations.form.projectName"), {
              required: true,
              mono: true,
            })}
            <div>
              {renderLabel(i18n.t("aeros.integrations.form.language"), {
                required: true,
              })}
              <TextField
                select
                value={integration.language || ""}
                onChange={(e) => change("language", e.target.value)}
                variant="outlined"
                fullWidth
                className={classes.input}
              >
                <MenuItem value="pt-BR">Português (pt-BR)</MenuItem>
                <MenuItem value="en">English (en)</MenuItem>
                <MenuItem value="es">Español (es)</MenuItem>
              </TextField>
            </div>
          </div>
          {campo("jsonContent", i18n.t("aeros.integrations.form.jsonContent"), {
            required: true,
            multiline: 6,
            mono: true,
          })}
          <div className={classes.testBlock}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.testTitle}>
                {i18n.t("aeros.integrations.dialogflow.test")}
              </div>
            </div>
            <Button
              className={classes.testButton}
              startIcon={testing ? undefined : <SendIcon />}
              onClick={handleTestSession}
              disabled={testing}
            >
              {testing ? (
                <CircularProgress size={14} />
              ) : (
                i18n.t("aeros.integrations.test.button")
              )}
            </Button>
          </div>
          {testResult?.session && (
            <div
              className={`${classes.testResult} ${
                testResult.ok ? classes.testOk : classes.testFail
              }`}
            >
              {testResult.ok ? <CheckCircleIcon /> : <ErrorIcon />}
              <span>
                {i18n.t(
                  testResult.ok
                    ? "aeros.integrations.dialogflow.ok"
                    : "aeros.integrations.dialogflow.fail"
                )}
              </span>
            </div>
          )}
        </>
      );
    }

    if (meta.key === "SGP") {
      return (
        <>
          <div>
            {renderLabel(i18n.t("aeros.integrations.form.sgpIeSenha"), {
              required: true,
            })}
            <TextField
              select
              value={integration.sgpIeSenha || ""}
              onChange={(e) => change("sgpIeSenha", e.target.value)}
              variant="outlined"
              fullWidth
              className={classes.input}
            >
              {SGP_PASSWORD_OPTIONS.map((opcao) => (
                <MenuItem key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {campo("sgpUrl", i18n.t("aeros.integrations.form.sgpUrl"), {
            required: true,
            mono: true,
            placeholder: "https://suaempresa.sgp.net.br",
          })}
          {campo("sgpWidePayToken", i18n.t("aeros.integrations.form.sgpWidePayToken"), {
            mono: true,
            hint: i18n.t("aeros.integrations.form.sgpWidePayHelper"),
          })}
        </>
      );
    }

    // flowbuilder: só nome — o fluxo em si é montado no editor do Flowbuilder.
    return null;
  };

  // Painel lateral: reaproveita a documentação da tela, para o contrato ficar
  // descrito em um lugar só.
  const doc = INTEGRATION_DOCS.find((d) =>
    d.id === (ehWebhook ? "n8n" : meta.key.toLowerCase())
  );

  const renderPanel = () => {
    if (!doc) return null;
    const aba = doc.tabs.find((t) => t.kind === "code") || doc.tabs[0];
    const linhas =
      aba.kind === "campos"
        ? [aba.header, "{", ...aba.lines.map((l) => `  ${l}`), "}"].join("\n")
        : String(aba.source);
    return (
      <div className={classes.right}>
        <div className={classes.panelTitle}>
          {i18n.t(
            ehWebhook
              ? "aeros.integrations.panel.n8n"
              : meta.key === "SGP"
              ? "aeros.integrations.panel.sgp"
              : "aeros.integrations.panel.typebot"
          )}
        </div>
        <pre className={classes.panelCode}>{linhas}</pre>
        <div className={classes.panelIntro}>{doc.intro}</div>
      </div>
    );
  };

  const footer = (
    <div className={classes.footer}>
      <span className={classes.footerHint}>
        {i18n.t("aeros.integrations.modal.footerHint")}
      </span>
      <div className={classes.footerActions}>
        <Button className={classes.cancelButton} onClick={handleClose} disabled={saving}>
          {i18n.t("aeros.flow.cancel")}
        </Button>
        <Button
          className={classes.saveButton}
          onClick={handleSave}
          disabled={saving || !integration.name}
        >
          {saving ? (
            <CircularProgress size={18} />
          ) : (
            i18n.t("aeros.integrations.modal.save")
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={900}
      title={i18n.t(
        integrationId
          ? "aeros.integrations.modal.edit"
          : "aeros.integrations.modal.add"
      )}
      subtitle={i18n.t("aeros.integrations.modal.subtitle")}
      footer={footer}
      flushBody
      loading={saving}
    >
      <div className={classes.head}>
        <div>
          {renderLabel(i18n.t("aeros.integrations.modal.type"), { required: true })}
          <div className={classes.typeGrid} role="radiogroup">
            {INTEGRATION_TYPES.map((tipo) => {
              const Icon = TYPE_ICONS[tipo.key] || WebhookIcon;
              const ativo = meta.key === tipo.key;
              return (
                <button
                  key={tipo.key}
                  type="button"
                  role="radio"
                  aria-checked={ativo}
                  className={classes.typeCard}
                  style={
                    ativo
                      ? {
                          borderColor: alpha(tipo.color, 0.6),
                          backgroundColor: alpha(tipo.color, 0.1),
                          color: theme.palette.text.primary,
                        }
                      : undefined
                  }
                  onClick={() => {
                    change("type", tipo.key);
                    setTestResult(null);
                  }}
                >
                  <Icon
                    style={{
                      fontSize: 20,
                      color: ativo
                        ? stateText(theme, tipo.color, "")
                        : theme.palette.aeros.textTertiary,
                    }}
                  />
                  {typeLabel(tipo.key)}
                </button>
              );
            })}
          </div>
        </div>

        {campo("name", i18n.t("aeros.integrations.modal.name"), { required: true })}
      </div>

      <div className={`${classes.split} ${doc ? "" : classes.splitSingle}`}>
        <div className={classes.left}>{renderFields()}</div>
        {renderPanel()}
      </div>
    </AerosDialog>
  );
};

export default QueueIntegrationModal;
